import { motion } from 'framer-motion';
import { ArrowLeft, Check, LogOut, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { avatars, teamColors, TeamAvatar } from '../components/team/Avatar';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { callFunction, errorMessage } from '../lib/callables';
import { useAuth } from '../providers/AuthProvider';

export function RegisterPage({ admissionOpen }: { admissionOpen: boolean }) {
  const { teamId, logout } = useAuth();
  const [name, setName] = useState('');
  const [members, setMembers] = useState(['', '', '']);
  const [slogan, setSlogan] = useState('');
  const [avatarId, setAvatar] = useState<(typeof avatars)[number][0]>('query-architect');
  const [color, setColor] = useState<(typeof teamColors)[number]>('#F26A21');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const valid = useMemo(() => name.trim().length >= 2 && (members[0] ?? '').trim().length >= 2 && (members[1] ?? '').trim().length >= 2, [members, name]);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setError('');
    try {
      await callFunction('registerTeam', {
        name: name.trim(),
        members: members.map((member) => member.trim()).filter(Boolean),
        slogan: slogan.trim(),
        avatarId,
        color,
      });
    } catch (reason) {
      setError(errorMessage(reason));
      setBusy(false);
    }
  };

  if (!admissionOpen) {
    return (
      <main className="grid min-h-screen place-items-center bg-ink px-6 text-center text-white">
        <div><BrandLockup inverse /><h1 className="mt-10 text-3xl font-black">ثبت تیم فعلاً بسته شده است</h1><p className="mt-4 text-slate-400">برای باز شدن دوباره ورود، با مدیر سالن هماهنگ کنید.</p><Button variant="secondary" className="mt-8" onClick={() => void logout()}>خروج از کد</Button></div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F5F6F8]">
      <header className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8">
        <BrandLockup />
        <button className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-ink" onClick={() => void logout()}><LogOut className="size-4" /> خروج از کد تیم</button>
      </header>
      <section className="mx-auto grid max-w-7xl gap-10 px-5 pb-12 pt-7 lg:grid-cols-[.7fr_1.3fr] lg:px-8">
        <motion.aside initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-[26px] bg-ink p-8 text-white shadow-lift lg:sticky lg:top-8 lg:h-[620px]">
          <div className="arena-grid absolute inset-0 opacity-50" />
          <div className="relative">
            <span className="text-xs font-bold text-brand">هویت تیم</span>
            <h1 className="mt-4 text-4xl font-black leading-tight">اسمی که روی<br />تابلوی سالن می‌ماند.</h1>
            <p className="mt-5 text-sm leading-7 text-slate-400">دو یا سه نفر، یک لپ‌تاپ، یک کد تیم؛ همین ترکیب وارد مسابقه می‌شود.</p>
            <div className="mt-12 border-y border-white/10 py-7">
              <div className="flex items-center gap-4"><TeamAvatar avatarId={avatarId} color={color} className="size-16" /><div><b className="block text-lg">{name || 'نام تیم شما'}</b><span className="text-xs text-white/45">{avatars.find(([id]) => id === avatarId)?.[1]}</span></div></div>
              <p className="mt-5 min-h-6 text-sm italic text-white/60">{slogan || 'شعار تیم اینجا می‌درخشد…'}</p>
            </div>
            <div className="mt-7 flex items-center gap-3 text-xs text-emerald-300"><Check className="size-4" /> شناسه تیم آماده: <span dir="ltr" className="font-mono">{teamId?.slice(0, 8)}</span></div>
          </div>
        </motion.aside>

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="py-3">
          <div className="mb-8"><p className="text-xs font-black text-brand">ترکیب تیم / ۰۱</p><h2 className="mt-2 text-3xl font-black text-ink">بازیکن‌ها را معرفی کنید</h2></div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="md:col-span-2"><span className="mb-2 block text-xs font-bold text-slate-600">نام تیم</span><Input value={name} onChange={(event) => setName(event.target.value)} maxLength={40} placeholder="مثلاً: NULL Hunters" /></label>
            {members.map((member, index) => (
              <label key={index} className={index === 2 ? 'md:col-span-2' : ''}><span className="mb-2 block text-xs font-bold text-slate-600">بازیکن {index + 1} {index === 2 && <em className="font-normal not-italic text-slate-400">(اختیاری)</em>}</span><Input value={member} onChange={(event) => setMembers((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} placeholder="نام بازیکن" /></label>
            ))}
            <label className="md:col-span-2"><span className="mb-2 block text-xs font-bold text-slate-600">شعار تیم <em className="font-normal not-italic text-slate-400">(اختیاری)</em></span><Input value={slogan} onChange={(event) => setSlogan(event.target.value)} maxLength={100} placeholder="یک جمله کوتاه برای لحظه اعلام نتایج" /></label>
          </div>

          <div className="mt-10"><p className="text-xs font-black text-brand">نشان تیم / ۰۲</p><h3 className="mt-2 text-xl font-black">یک شخصیت برای تیم انتخاب کنید</h3><div className="mt-5 grid grid-cols-3 gap-3 md:grid-cols-4">
            {avatars.map(([id, label]) => <button key={id} type="button" onClick={() => setAvatar(id)} className={`group relative rounded-2xl border bg-white px-2 py-4 text-center transition ${avatarId === id ? 'border-brand shadow-glow' : 'border-slate-200 hover:border-slate-300'}`}><TeamAvatar avatarId={id} color={color} className="mx-auto" /><span className="mt-2 block text-[10px] font-bold text-slate-600">{label}</span>{avatarId === id && <i className="absolute left-2 top-2 grid size-5 place-items-center rounded-full bg-brand text-white"><Check className="size-3" /></i>}</button>)}
          </div></div>

          <div className="mt-8"><span className="mb-3 block text-xs font-bold text-slate-600">رنگ نشان تیم</span><div className="flex flex-wrap gap-3">{teamColors.map((item) => <button key={item} onClick={() => setColor(item)} className="grid size-10 place-items-center rounded-full ring-offset-4 transition" style={{ backgroundColor: item, boxShadow: color === item ? `0 0 0 3px white, 0 0 0 5px ${item}` : undefined }} aria-label={`انتخاب رنگ ${item}`}>{color === item && <Check className="size-4 text-white" />}</button>)}</div></div>
          {error && <p role="alert" className="mt-6 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <div className="mt-10 flex items-center justify-between border-t border-slate-200 pt-6"><span className="flex items-center gap-2 text-xs text-slate-400"><Users className="size-4" /> حداقل ۲ و حداکثر ۳ بازیکن</span><Button size="lg" disabled={!valid || busy} onClick={() => void submit()}>{busy ? 'در حال ثبت…' : 'ثبت تیم و رفتن به لابی'}<ArrowLeft className="size-4" /></Button></div>
        </motion.div>
      </section>
    </main>
  );
}
